import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { useTranslation } from '../LanguageContext';
import {
  Users, Car, Eye, Activity, TrendingUp, BarChart3,
  Globe, Clock, ArrowLeft, RefreshCw, FileText, Zap, Loader2, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#00FFFF', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];

const AdminPage = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pageviews, setPageviews] = useState([]);
  const [pages, setPages] = useState([]);
  const [browsers, setBrowsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [magArticles, setMagArticles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genCategory, setGenCategory] = useState('');
  const [genType, setGenType] = useState('expert');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pvRes, pagesRes, browsersRes, usersRes, actRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/pageviews?days=30`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/pages`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/browsers`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/users`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/analytics/activity`, { headers, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setPageviews(pvRes.data);
      setPages(pagesRes.data);
      setBrowsers(browsersRes.data);
      setUsers(usersRes.data);
      setActivity(actRes.data);
    } catch (error) {
      console.error('Admin fetch error:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'magazine') {
      axios.get(`${API}/magazine/articles`, { params: { limit: 50 } })
        .then(r => setMagArticles(r.data)).catch(() => {});
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = stats ? [
    { label: t('admin.totalUsers'), value: stats.total_users, icon: Users, color: 'from-cyan-500/20 to-cyan-600/20', border: 'border-cyan-500/20' },
    { label: t('admin.totalCars'), value: stats.total_cars, icon: Car, color: 'from-green-500/20 to-green-600/20', border: 'border-green-500/20' },
    { label: t('admin.pageViews'), value: stats.total_pageviews, icon: Eye, color: 'from-purple-500/20 to-purple-600/20', border: 'border-purple-500/20' },
    { label: t('admin.sessions'), value: stats.total_sessions, icon: Activity, color: 'from-orange-500/20 to-orange-600/20', border: 'border-orange-500/20' },
    { label: t('admin.todayViews'), value: stats.today_pageviews, icon: TrendingUp, color: 'from-blue-500/20 to-blue-600/20', border: 'border-blue-500/20' },
    { label: t('admin.todayRegs'), value: stats.today_registrations, icon: Users, color: 'from-pink-500/20 to-pink-600/20', border: 'border-pink-500/20' },
    { label: t('admin.newCarsWeek'), value: stats.new_cars_week, icon: Car, color: 'from-yellow-500/20 to-yellow-600/20', border: 'border-yellow-500/20' },
  ] : [];

  const tabs = [
    { id: 'overview', label: t('admin.overview') },
    { id: 'analytics', label: t('admin.analytics') },
    { id: 'users', label: t('admin.users') },
    { id: 'activity', label: t('admin.activity') },
    { id: 'magazine', label: 'Magazine' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#0a0a0f] border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-gray-400 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>{entry.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-2xl font-bold">
                <span className="text-cyan-500 neon-text">{t('admin.title')}</span>
              </h1>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="ghost" size="sm" className="text-gray-400 hover:text-cyan-500">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl bg-white/[0.02] border border-white/5 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-xs font-medium rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-cyan-500 text-black'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
              {statCards.map((card, i) => (
                <div key={i} className={`glass-card rounded-xl p-4 border ${card.border} bg-gradient-to-br ${card.color}`}>
                  <card.icon className="w-5 h-5 text-gray-400 mb-2" />
                  <div className="font-heading text-2xl font-bold text-white">{card.value}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{card.label}</div>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Page Views Chart */}
              <div className="glass-card rounded-xl p-5 border border-white/5">
                <h3 className="font-heading text-sm font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-cyan-500" />
                  {t('admin.dailyViews')}
                </h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={pageviews}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#666' }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="views" stroke="#00FFFF" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Browser Stats */}
              <div className="glass-card rounded-xl p-5 border border-white/5">
                <h3 className="font-heading text-sm font-semibold mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-cyan-500" />
                  {t('admin.browserStats')}
                </h3>
                {browsers.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={browsers} dataKey="count" nameKey="browser" cx="50%" cy="50%" outerRadius={80} label={({ browser, percent }) => `${browser} ${(percent * 100).toFixed(0)}%`}>
                        {browsers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-gray-600 text-sm">No browser data yet</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            {/* Popular Pages */}
            <div className="glass-card rounded-xl p-5 border border-white/5">
              <h3 className="font-heading text-sm font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-cyan-500" />
                {t('admin.popularPages')}
              </h3>
              {pages.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={pages.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#666' }} />
                    <YAxis dataKey="page" type="category" tick={{ fontSize: 10, fill: '#999' }} width={150} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="views" fill="#00FFFF" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-gray-600 text-sm">No page view data yet. Analytics will populate as users visit the site.</div>
              )}
            </div>

            {/* Full Pageview Chart */}
            <div className="glass-card rounded-xl p-5 border border-white/5">
              <h3 className="font-heading text-sm font-semibold mb-4">{t('admin.dailyViews')} (30 days)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={pageviews}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#666' }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="views" stroke="#00FFFF" strokeWidth={2} dot={false} fill="rgba(0,255,255,0.1)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="glass-card rounded-xl border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-500" />
                {t('admin.recentUsers')}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Name</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Email</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Role</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={i} className="border-b border-white/3 hover:bg-white/3">
                      <td className="px-4 py-3 text-xs text-white">{u.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.is_admin ? 'bg-red-500/20 text-red-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                          {u.is_admin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Cars */}
            <div className="glass-card rounded-xl border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
                  <Car className="w-4 h-4 text-cyan-500" />
                  Recent Car Listings
                </h3>
              </div>
              <div className="divide-y divide-white/5">
                {activity?.recent_cars?.map((car, i) => (
                  <div key={i} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-white font-medium">{car.title}</div>
                      <div className="text-[10px] text-gray-500">by {car.seller_name}</div>
                    </div>
                    <div className="text-xs text-cyan-500 font-semibold">€{car.price?.toLocaleString()}</div>
                  </div>
                ))}
                {(!activity?.recent_cars || activity.recent_cars.length === 0) && (
                  <div className="px-4 py-8 text-center text-gray-600 text-xs">No recent car listings</div>
                )}
              </div>
            </div>

            {/* Recent Events */}
            <div className="glass-card rounded-xl border border-white/5 overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-500" />
                  {t('admin.recentActivity')}
                </h3>
              </div>
              <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
                {activity?.events?.slice(0, 20).map((ev, i) => (
                  <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${ev.event_type === 'pageview' ? 'bg-cyan-500' : ev.event_type === 'registration' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-white truncate">{ev.event_type}: {ev.page || 'N/A'}</div>
                      <div className="text-[9px] text-gray-600">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}</div>
                    </div>
                  </div>
                ))}
                {(!activity?.events || activity.events.length === 0) && (
                  <div className="px-4 py-8 text-center text-gray-600 text-xs">No activity yet</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Magazine Tab */}
        {activeTab === 'magazine' && (
          <MagazineAdmin token={token} articles={magArticles} setArticles={setMagArticles} />
        )}
      </div>
    </div>
  );
};

/* ============ Magazine Admin ============ */
const MagazineAdmin = ({ token, articles, setArticles }) => {
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genCategory, setGenCategory] = useState('');
  const [genType, setGenType] = useState('expert');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ title: '', content: '', category: 'Future', meta_description: '', tags: '', status: 'published' });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const cats = ['Future','Money','System','Power','Style','Mind','AI Insights'];

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/magazine/generate`, { topic: genTopic, category: genCategory, schedule_type: genType }, { headers });
      setArticles(prev => [r.data, ...prev]);
      toast.success(`Generated: ${r.data.title}`);
      setGenTopic('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const createManual = async () => {
    if (!manualForm.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/magazine/articles/manual`, {
        ...manualForm, tags: manualForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      }, { headers });
      setArticles(prev => [r.data, ...prev]);
      toast.success('Article created');
      setShowManual(false);
      setManualForm({ title: '', content: '', category: 'Future', meta_description: '', tags: '', status: 'published' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const startEdit = (a) => {
    setEditId(a.id);
    setEditForm({ title: a.title, content: a.content, category: a.category, meta_description: a.meta_description || '', tags: (a.tags || []).join(', '), status: a.status || 'published' });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/magazine/articles/${editId}`, {
        ...editForm, tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      }, { headers });
      setArticles(prev => prev.map(a => a.id === editId ? r.data : a));
      toast.success('Updated');
      setEditId(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await axios.delete(`${API}/magazine/articles/${id}`, { headers });
    setArticles(prev => prev.filter(a => a.id !== id));
    toast.success('Deleted');
  };

  const toggleStatus = async (a) => {
    const newStatus = (a.status || 'published') === 'published' ? 'draft' : 'published';
    await axios.put(`${API}/magazine/articles/${a.id}`, { status: newStatus }, { headers });
    setArticles(prev => prev.map(x => x.id === a.id ? { ...x, status: newStatus } : x));
    toast.success(newStatus === 'published' ? 'Published' : 'Moved to draft');
  };

  // Edit view
  if (editId) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-semibold">Edit Article</h3>
          <Button variant="outline" size="sm" onClick={() => setEditId(null)} className="text-xs border-white/10">Cancel</Button>
        </div>
        <div className="space-y-3">
          <div><label className="text-[10px] text-gray-500 mb-1 block">Title</label><Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} className="h-9 text-sm bg-black/30 border-white/10" /></div>
          <div><label className="text-[10px] text-gray-500 mb-1 block">Meta Description</label><Input value={editForm.meta_description} onChange={e => setEditForm(p => ({ ...p, meta_description: e.target.value }))} maxLength={160} className="h-9 text-sm bg-black/30 border-white/10" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] text-gray-500 mb-1 block">Category</label><select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} className="w-full h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300">{cats.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="text-[10px] text-gray-500 mb-1 block">Status</label><select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} className="w-full h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300"><option value="published">Published</option><option value="draft">Draft</option></select></div>
          </div>
          <div><label className="text-[10px] text-gray-500 mb-1 block">Tags (comma-separated)</label><Input value={editForm.tags} onChange={e => setEditForm(p => ({ ...p, tags: e.target.value }))} className="h-9 text-sm bg-black/30 border-white/10" /></div>
          <div><label className="text-[10px] text-gray-500 mb-1 block">Content (Markdown)</label><textarea value={editForm.content} onChange={e => setEditForm(p => ({ ...p, content: e.target.value }))} rows={16} className="w-full text-sm bg-black/30 border border-white/10 rounded-md p-3 text-gray-300 font-mono resize-y" /></div>
          <Button onClick={saveEdit} disabled={saving} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs h-9">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save Changes</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* AI Generate */}
      <div className="rounded-xl p-5 bg-white/[0.02] border border-white/5 mb-4">
        <h3 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-cyan-500" /> AI Generate</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <Input value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="Topic (optional)" className="h-9 text-sm bg-black/30 border-white/10" />
          <select value={genCategory} onChange={e => setGenCategory(e.target.value)} className="h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300"><option value="">Any Category</option>{cats.map(c => <option key={c} value={c}>{c}</option>)}</select>
          <select value={genType} onChange={e => setGenType(e.target.value)} className="h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300"><option value="expert">Expert (08:00)</option><option value="provocative">Provocative (14:00)</option><option value="lifestyle">Lifestyle (20:00)</option></select>
        </div>
        <Button onClick={generate} disabled={generating} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs h-9">
          {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Generating...</> : <><FileText className="w-3.5 h-3.5 mr-1" /> Generate Article</>}
        </Button>
      </div>

      {/* Manual Create */}
      {showManual ? (
        <div className="rounded-xl p-5 bg-white/[0.02] border border-white/5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold">Write Article</h3>
            <button onClick={() => setShowManual(false)} className="text-xs text-gray-500 hover:text-white">Cancel</button>
          </div>
          <div className="space-y-3">
            <Input value={manualForm.title} onChange={e => setManualForm(p => ({ ...p, title: e.target.value }))} placeholder="Article Title" className="h-9 text-sm bg-black/30 border-white/10" />
            <Input value={manualForm.meta_description} onChange={e => setManualForm(p => ({ ...p, meta_description: e.target.value }))} placeholder="Meta Description (150 chars)" maxLength={160} className="h-9 text-sm bg-black/30 border-white/10" />
            <div className="grid grid-cols-3 gap-3">
              <select value={manualForm.category} onChange={e => setManualForm(p => ({ ...p, category: e.target.value }))} className="h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300">{cats.map(c => <option key={c} value={c}>{c}</option>)}</select>
              <select value={manualForm.status} onChange={e => setManualForm(p => ({ ...p, status: e.target.value }))} className="h-9 px-3 text-sm bg-black/30 border border-white/10 rounded-md text-gray-300"><option value="published">Published</option><option value="draft">Draft</option></select>
              <Input value={manualForm.tags} onChange={e => setManualForm(p => ({ ...p, tags: e.target.value }))} placeholder="Tags (comma)" className="h-9 text-sm bg-black/30 border-white/10" />
            </div>
            <textarea value={manualForm.content} onChange={e => setManualForm(p => ({ ...p, content: e.target.value }))} rows={12} placeholder="Article content (Markdown supported)..." className="w-full text-sm bg-black/30 border border-white/10 rounded-md p-3 text-gray-300 font-mono resize-y" />
            <Button onClick={createManual} disabled={saving} className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs h-9">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Publish Article</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setShowManual(true)} className="text-xs h-9 border-white/10 mb-4"><FileText className="w-3.5 h-3.5 mr-1" /> Write Manual Article</Button>
      )}

      {/* Articles List */}
      <h3 className="font-heading text-sm font-semibold mb-3">Articles ({articles.length})</h3>
      {articles.length === 0 ? (
        <div className="text-center py-10 rounded-xl bg-white/[0.02] border border-white/5">
          <img src="/hipo_thinking.webp" alt="" className="w-12 h-12 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No articles yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
              <img src={`/${a.avatar}.webp`} alt="" className="w-8 h-8 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold truncate">{a.title}</p>
                  {(a.status || 'published') === 'draft' && <span className="text-[8px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded shrink-0">DRAFT</span>}
                </div>
                <p className="text-[10px] text-gray-500">{a.category} &middot; {a.read_time_minutes}min &middot; {a.views} views &middot; {new Date(a.published_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleStatus(a)} className={`text-[10px] px-2 py-1 rounded ${(a.status || 'published') === 'published' ? 'text-green-400 bg-green-500/10' : 'text-yellow-400 bg-yellow-500/10'}`}>{(a.status || 'published') === 'published' ? 'Live' : 'Draft'}</button>
                <button onClick={() => startEdit(a)} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-cyan-400 rounded hover:bg-white/5"><FileText className="w-3.5 h-3.5" /></button>
                <a href={`/magazine/${a.slug}`} target="_blank" rel="noreferrer" className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-cyan-400 rounded hover:bg-white/5"><Eye className="w-3.5 h-3.5" /></a>
                <button onClick={() => deleteArticle(a.id)} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-red-400 rounded hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
